
import { Suspense, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4bu73moquaeac4hdv90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Contact Digital Experience Studio - Premium Social Media Marketing Services');
    
    const shareUrls: { [key: string]: string } = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Have questions? Want to discuss your social media needs? We'd love to hear from you! Reach out and let's start a conversation.
            </p>
            
            {/* Share and Copy Links */}
            <div className="flex justify-center items-center space-x-4 mb-6">
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
                >
                  <i className="ri-share-line text-blue-600"></i>
                  <span className="text-gray-700 font-semibold">Share</span>
                </button>
                
                {showShareMenu && (
                  <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 z-50 whitespace-nowrap">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-full hover:bg-blue-700 transition-colors cursor-pointer"
                        title="Share on Facebook"
                      >
                        <i className="ri-facebook-fill text-white text-lg"></i>
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-gray-900 transition-colors cursor-pointer"
                        title="Share on Twitter"
                      >
                        <i className="ri-twitter-x-fill text-white text-lg"></i>
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-10 h-10 flex items-center justify-center bg-blue-700 rounded-full hover:bg-blue-800 transition-colors cursor-pointer"
                        title="Share on LinkedIn"
                      >
                        <i className="ri-linkedin-fill text-white text-lg"></i>
                      </button>
                      <button
                        onClick={() => handleShare('email')}
                        className="w-10 h-10 flex items-center justify-center bg-gray-600 rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
                        title="Share via Email"
                      >
                        <i className="ri-mail-line text-white text-lg"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleCopyLink}
                className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
              >
                <i className={`${copySuccess ? 'ri-check-line text-green-600' : 'ri-link text-blue-600'}`}></i>
                <span className="text-gray-700 font-semibold">
                  {copySuccess ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
                <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                  Send Us a Message
                </h2>
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="mb-6">
                    <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-gray-700 font-semibold mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="services">Services Question</option>
                      <option value="pricing">Pricing Information</option>
                      <option value="support">Customer Support</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm resize-none"
                      placeholder="Tell us how we can help you..."
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                      <i className="ri-check-line mr-2"></i>
                      Thank you for contacting us! We'll get back to you within 24 hours.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl">
                      <i className="ri-error-warning-line mr-2"></i>
                      Oops! Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl">
                  <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                    Contact Information
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl mr-4 flex-shrink-0">
                        <i className="ri-mail-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-800 mb-1">Email</div>
                        <a href="mailto:letschat@socialswingstudio.com" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                          letschat@socialswingstudio.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-xl mr-4 flex-shrink-0">
                        <i className="ri-phone-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-800 mb-1">Phone</div>
                        <a href="tel:+17606556009" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                          (760) 655-6009
                        </a>
                        <p className="text-sm text-gray-500 mt-1">
                          Current clients: Call during office hours for live support
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl mr-4 flex-shrink-0">
                        <i className="ri-time-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <div className="font-bold text-gray-800 mb-1">Office Hours</div>
                        <div className="text-gray-600">
                          Monday - Friday: 8:00 AM - 6:00 PM
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Live support available for current clients during office hours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl">
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Follow Us</h3>
                  <p className="text-gray-600 mb-6">
                    Stay connected and see our latest work on social media!
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-facebook-fill text-2xl text-white"></i>
                    </a>
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-instagram-fill text-2xl text-white"></i>
                    </a>
                    <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-twitter-x-fill text-2xl text-white"></i>
                    </a>
                    <a href="https://www.yelp.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-red-600 to-red-500 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-yelp-fill text-2xl text-white"></i>
                    </a>
                    <a href="https://www.trustpilot.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-green-600 to-green-500 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-star-fill text-2xl text-white"></i>
                    </a>
                    <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="w-full h-14 flex items-center justify-center bg-gradient-to-r from-blue-700 to-blue-600 rounded-xl hover:shadow-lg transition-all cursor-pointer">
                      <i className="ri-linkedin-fill text-2xl text-white"></i>
                    </a>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-8 text-center shadow-xl">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Prefer to Talk?
                  </h3>
                  <p className="text-white/90 mb-6">
                    Book a free consultation and let's discuss your needs in person!
                  </p>
                  <a
                    href="/consultation"
                    className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Schedule Free Consultation
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
